import { Platform, View } from "@-/com.npmjs.react-native";
import {
  GestureHandlerRootView,
  type LegacyPanGesture,
} from "@-/com.npmjs.react-native-gesture-handler";
import Animated, {
  type DerivedValue,
  useAnimatedStyle,
} from "@-/com.npmjs.react-native-reanimated";
import { FullWindowOverlay } from "@-/com.npmjs.react-native-screens";
import { BottomSheetModalProvider } from "@-/dev.improve.haniverse.components.ui.bottom-sheet-provider";
import {
  TopSheet,
  type useTopSheetRevealMotion,
} from "@-/dev.improve.haniverse.components.ui.top-sheet";
import type { ReactNode } from "react";
import { AdminPanel } from "#admin-panel";
import {
  ADMIN_PANEL_HANDLE_HEIGHT,
  type AdminPanelPlacement,
  type AdminPanelTab,
} from "#admin-panel-layout";

/** A nonmodal reveal anchored at the top or bottom edge. Only the exposed surface receives touches. */
export function AdminPanelSheet({
  bottomEdgeY,
  motion,
  onPlacementChange,
  onTabChange,
  placement,
  renderDrawerButton,
  shellTranslateX,
  tab,
}: {
  /** The window y of the bottom placement's lower edge, above the bottom chrome. */
  bottomEdgeY: number;
  motion: ReturnType<typeof useTopSheetRevealMotion>;
  onPlacementChange: (placement: AdminPanelPlacement) => void;
  onTabChange: (tab: AdminPanelTab) => void;
  placement: AdminPanelPlacement;
  renderDrawerButton?: (grip: LegacyPanGesture) => ReactNode;
  shellTranslateX?: DerivedValue<number>;
  tab: AdminPanelTab;
}) {
  const isTop = placement === "top";
  // The profile drawer slides the whole app shell aside; the panel slides with it, uncovering the
  // drawer instead of hiding it.
  const shellStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: shellTranslateX?.get() ?? 0 }],
  }));
  const panel = (
    <View className="absolute inset-0 z-50" pointerEvents="box-none" testID="admin-panel-overlay">
      <GestureHandlerRootView className="flex-1" pointerEvents="box-none">
        <BottomSheetModalProvider>
          <Animated.View className="flex-1" pointerEvents="box-none" style={shellStyle}>
            <View
              className={isTop ? "absolute inset-0" : "absolute inset-x-0 top-0"}
              pointerEvents="box-none"
              style={isTop ? undefined : { height: bottomEdgeY }}
              testID="admin-panel-frame"
            >
              <TopSheet
                accessibilityLabel="Admin panel"
                edge={placement}
                handle={
                  <View
                    accessibilityHint={
                      isTop
                        ? "Drag down to expand or up to resize; drag to the top to dismiss"
                        : "Drag up to expand or down to resize; drag to the bottom to dismiss"
                    }
                    accessibilityLabel="Admin panel handle"
                    className="shrink-0 items-center justify-center"
                    style={{ height: ADMIN_PANEL_HANDLE_HEIGHT }}
                    testID="admin-panel-handle"
                  >
                    <View className="h-1 w-10 rounded-full bg-muted-foreground/40" />
                  </View>
                }
                mode="reveal"
                motion={motion}
                testID="admin-panel"
              >
                <AdminPanel
                  handleHeight={ADMIN_PANEL_HANDLE_HEIGHT}
                  onClose={motion.close}
                  onPlacementChange={onPlacementChange}
                  onTabChange={onTabChange}
                  placement={placement}
                  tab={tab}
                  // At the bottom the app's own drawer button stays uncovered at the top.
                  trailingControl={isTop ? renderDrawerButton?.(motion.grip) : undefined}
                />
              </TopSheet>
            </View>
          </Animated.View>
        </BottomSheetModalProvider>
      </GestureHandlerRootView>
    </View>
  );
  // iOS needs a window-level host above native screens. Android stays in the shell's root view:
  // a transparent Modal owns its entire dialog window and prevents touch-through to the app.
  return Platform.OS === "ios" ? <FullWindowOverlay>{panel}</FullWindowOverlay> : panel;
}
